import { useState } from 'react';
import { useCreateUser, useUpdateUser } from '@/features/users/hooks/useUsers';
import { useApiErrorToast } from '@/shared/ui/Toast';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { useModalStyles } from '@/shared/ui/modalStyles';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { brand, radius, semantic, typography } from '@/shared/theme/tokens';
import { ROLES } from '@/entities/rbac/lib/permissions';

const GLOBAL_ROLE_OPTIONS = [
  { value: ROLES.RIPIK_ADMIN, label: 'Admin' },
  { value: ROLES.RIPIK_USER, label: 'User' },
];

const EMPTY_FORM = { name: '', email: '', role: ROLES.RIPIK_USER };

export function UserFormModal({ mode, user, onClose }) {
  const c = useThemeColors();
  const { overlayStyle, modalStyle } = useModalStyles('480px');

  const existingRole = user?.assignments?.some((a) => a.role === ROLES.RIPIK_ADMIN)
    ? ROLES.RIPIK_ADMIN
    : ROLES.RIPIK_USER;

  const [form, setForm] = useState(
    mode === 'edit' && user
      ? { name: user.name, email: user.email, role: existingRole }
      : EMPTY_FORM
  );
  const [errors, setErrors] = useState({});

  const toastApiError = useApiErrorToast();
  const { mutateAsync: create, isPending: creating } = useCreateUser();
  const { mutateAsync: update, isPending: updating } = useUpdateUser();
  const pending = creating || updating;

  function setField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const payload = { name: form.name, email: form.email, role: form.role };
    try {
      if (mode === 'create') {
        await create(payload);
      } else {
        await update({ id: user.id, payload });
      }
      onClose();
    } catch (err) { toastApiError(err); }
  }

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: 40, height: 40, borderRadius: radius.lg, background: 'linear-gradient(135deg, rgba(32,36,117,0.08), rgba(62,181,231,0.08))', border: `1px solid ${semantic.brandBorderAccent}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: brand.navy, fontVariationSettings: "'FILL' 1" }}>person</span>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading }}>
                {mode === 'create' ? 'Add User' : 'Edit User'}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: typography.fontSize.base, color: c.textMuted }}>
                {mode === 'create' ? 'Create a new platform user' : 'Update user details and access'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: c.textSubtle }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Name */}
          <FormField label="Full Name" error={errors.name} required c={c}>
            <input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Jane Operator" style={fi(!!errors.name, c)} />
          </FormField>

          {/* Email */}
          <FormField label="Email Address" error={errors.email} required c={c}>
            <input value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="jane@enterprise.com" type="email" style={fi(!!errors.email, c)} />
          </FormField>

          {/* Role — single global role, no per-deployment toggle */}
          <FormField label="Role" c={c}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {GLOBAL_ROLE_OPTIONS.map((ro) => (
                <button
                  key={ro.value}
                  type="button"
                  onClick={() => setField('role', ro.value)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: radius.lg,
                    border: form.role === ro.value ? '1.5px solid rgba(62,181,231,0.5)' : `1.5px solid ${c.borderInput}`,
                    background: form.role === ro.value ? 'linear-gradient(135deg, rgba(32,36,117,0.07), rgba(62,181,231,0.07))' : c.bgSubtle,
                    color: form.role === ro.value ? brand.navy : c.textMuted,
                    fontSize: typography.fontSize.md,
                    fontWeight: form.role === ro.value ? 700 : 400,
                    fontFamily: typography.fontFamily.body,
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.12s',
                  }}
                >
                  {ro.label}
                </button>
              ))}
            </div>
          </FormField>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: `1px solid ${c.divider}` }}>
            <PrimaryButton variant="secondary" onClick={onClose} disabled={pending}>Cancel</PrimaryButton>
            <PrimaryButton type="submit" disabled={pending} icon={mode === 'create' ? 'person_add' : 'check'}>
              {pending ? (mode === 'create' ? 'Adding…' : 'Saving…') : (mode === 'create' ? 'Add User' : 'Save Changes')}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, children, error, required, c }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <label style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: c.textLabel, letterSpacing: typography.letterSpacing.label, textTransform: 'uppercase' }}>
        {label}{required && <span style={{ color: semantic.error, marginLeft: '3px' }}>*</span>}
      </label>
      {children}
      {error && <p style={{ fontSize: typography.fontSize.sm, color: semantic.error, margin: 0, fontWeight: typography.fontWeight.medium }}>{error}</p>}
    </div>
  );
}

function fi(hasError, c) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    background: c.bgSubtle,
    border: `1.5px solid ${hasError ? semantic.error : c.borderInput}`,
    borderRadius: radius.lg,
    padding: '12px 14px',
    minHeight: '44px',
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.body,
    color: c.textPrimary,
    outline: 'none',
  };
}
